import { derived, get, writable } from 'svelte/store';
import type { DashPromoSampleDefinition } from 'src/components/Dashboards/dash-promo-types';

const MAX_BANNERS_KEY = 'shelly_dev_dash_promo_max_banners';
const SAMPLE_IDS_KEY = 'shelly_dev_dash_promo_sample_ids_v2';
const DISMISSALS_KEY = 'shelly_dev_dash_promo_dismissals';
/** Legacy key (string[]); migrated into DISMISSALS_KEY. */
const LEGACY_DISMISSED_IDS_KEY = 'shelly_dev_dash_promo_dismissed_ids';

export const DASH_PROMO_MAX_BANNERS_DEFAULT = 5;

/** Catalog of sample generic banners (full promo campaign images as backgrounds). */
export const DASH_PROMO_SAMPLE_CATALOG: DashPromoSampleDefinition[] = [
	{
		id: 'sample-spring-deals',
		title: 'Spring Deals',
		message: 'Fresh season. Best prices. Up to 50% off.',
		image: './images/shelly_spring_deals_2026_App_default.png',
		ctaLabel: 'Learn more',
		ctaPosition: 'bottom-left',
		ctaHref: 'https://www.shelly.com',
	},
	{
		id: 'sample-wall-display',
		title: 'Wall Display Flash Sale',
		message: 'An exclusive offer for our most loyal users.',
		image: './images/wall_display_flash__deal_default.png',
		ctaLabel: 'Shop now',
		ctaPosition: 'bottom-left',
		ctaHref: 'https://www.shelly.com',
	},
	{
		id: 'sample-xl-x2',
		title: 'XL x2 Flash Deal',
		message: 'Limited-time deal on Shelly XL devices.',
		image: './images/xl_x2_flash_deal_default.png',
		ctaLabel: 'See deal',
		ctaPosition: 'bottom-right',
		ctaHref: 'https://www.shelly.com',
	},
	{
		id: 'sample-birthday',
		title: '8 years deserve big deals',
		message: 'Celebrate with us and enjoy up to 50% off, free gifts, and more.',
		image: './images/Shelly_BD_8y_KV_Adaptations_for_App_default.png',
		ctaLabel: 'Celebrate',
		ctaPosition: 'bottom-center',
		ctaHref: 'https://www.shelly.com',
	},
	{
		id: 'sample-30m',
		title: '30 Million Devices',
		message: 'Join millions of homes powered by Shelly.',
		image: './images/30m_default.png',
		ctaLabel: 'Discover',
		ctaPosition: 'bottom-left',
		ctaHref: 'https://www.shelly.com',
	},
	{
		id: 'sample-iot-challenge',
		title: 'IoT Challenge',
		message: 'Submit your project and win attractive prizes.',
		image: './images/ioT-Challenge-default.png',
		ctaLabel: 'Join now',
		ctaPosition: 'center',
		ctaHref: 'https://www.shelly.com',
	},
	{
		id: 'sample-color-orange',
		title: 'Warm orange backdrop',
		message: 'Generic banner on an app background color token.',
		image: '',
		colorToken: 'color-orange',
		ctaLabel: 'Open',
		ctaPosition: 'bottom-left',
		ctaHref: 'https://www.shelly.com',
	},
];

/** No sample campaigns until explicitly enabled in Dev Tools. */
const DEFAULT_SAMPLE_IDS: string[] = [];

const readNumber = (key: string, fallback: number): number => {
	try {
		const raw = localStorage.getItem(key);
		if (raw === null) return fallback;
		const n = Number(raw);
		return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
	} catch {
		return fallback;
	}
};

const readStringArray = (key: string, fallback: string[]): string[] => {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return fallback;
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : fallback;
	} catch {
		return fallback;
	}
};

/** id → dismissal epoch ms */
export type DashPromoDismissalsMap = Record<string, number>;

const readDismissalsMap = (): DashPromoDismissalsMap => {
	try {
		const raw = localStorage.getItem(DISMISSALS_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				const map: DashPromoDismissalsMap = {};
				for (const [id, ts] of Object.entries(parsed)) {
					if (typeof id === 'string' && typeof ts === 'number' && Number.isFinite(ts)) {
						map[id] = ts;
					}
				}
				return map;
			}
		}

		// Migrate legacy string[] of dismissed ids (no timestamps → use now).
		const legacy = readStringArray(LEGACY_DISMISSED_IDS_KEY, []);
		if (legacy.length > 0) {
			const now = Date.now();
			const map: DashPromoDismissalsMap = {};
			for (const id of legacy) map[id] = now;
			persist(DISMISSALS_KEY, map);
			try {
				localStorage.removeItem(LEGACY_DISMISSED_IDS_KEY);
			} catch {
				// ignore
			}
			return map;
		}
	} catch {
		// ignore
	}
	return {};
};

const persist = (key: string, value: unknown) => {
	try {
		localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
	} catch {
		// ignore
	}
};

/** Max banners the gallery will show (default 5). */
export const dashPromoMaxBanners = writable<number>(
	readNumber(MAX_BANNERS_KEY, DASH_PROMO_MAX_BANNERS_DEFAULT),
);

dashPromoMaxBanners.subscribe((value) => {
	persist(MAX_BANNERS_KEY, String(value));
});

/** Active sample banner ids from the catalog (Dev Modal managed, default none). */
export const dashPromoActiveSampleIds = writable<string[]>(
	readStringArray(SAMPLE_IDS_KEY, DEFAULT_SAMPLE_IDS),
);

dashPromoActiveSampleIds.subscribe((value) => {
	persist(SAMPLE_IDS_KEY, value);
});

/** Locally dismissed promo banner ids → dismissal timestamp (generic samples). */
export const dashPromoDismissals = writable<DashPromoDismissalsMap>(readDismissalsMap());

dashPromoDismissals.subscribe((value) => {
	persist(DISMISSALS_KEY, value);
});

/**
 * Dismissed sample ids (derived). Prefer dashPromoDismissals when you need timestamps.
 */
export const dashPromoDismissedIds = derived(dashPromoDismissals, (map) => Object.keys(map));

export const getDashPromoMaxBanners = (): number => get(dashPromoMaxBanners);

export const isDashPromoBannerDismissed = (id: string): boolean =>
	Boolean(get(dashPromoDismissals)[id]);

export const getDashPromoDismissalTimestamp = (id: string): number | null =>
	get(dashPromoDismissals)[id] ?? null;

export const dismissDashPromoBanner = (id: string): void => {
	dashPromoDismissals.update((map) => {
		if (map[id]) return map;
		return { ...map, [id]: Date.now() };
	});
};

export const clearDashPromoBannerDismissal = (id: string): void => {
	dashPromoDismissals.update((map) => {
		if (!(id in map)) return map;
		const next = { ...map };
		delete next[id];
		return next;
	});
};

export const clearAllDashPromoSampleDismissals = (): void => {
	dashPromoDismissals.set({});
};

export const addDashPromoSample = (id: string): void => {
	const exists = DASH_PROMO_SAMPLE_CATALOG.some((s) => s.id === id);
	if (!exists) return;
	dashPromoActiveSampleIds.update((ids) => (ids.includes(id) ? ids : [...ids, id]));
	clearDashPromoBannerDismissal(id);
};

export const removeDashPromoSample = (id: string): void => {
	dashPromoActiveSampleIds.update((ids) => ids.filter((x) => x !== id));
};

export const getActiveDashPromoSamples = (): DashPromoSampleDefinition[] => {
	const active = new Set(get(dashPromoActiveSampleIds));
	return DASH_PROMO_SAMPLE_CATALOG.filter((s) => active.has(s.id));
};
